import { hcr_saveHierarchyCompleted } from "contexts/hierarchy/functions/hcr_saveHierarchyCompleted";
import { hcr_updateHierarchy } from "contexts/hierarchy/functions/hcr_updateHierarchy";
import {
  IHierarchyActionSaveHierarchyCompleted,
  SAVE_HIERARCHY_COMPLETED,
  UPDATE_HIERARCHY,
} from "contexts/hierarchy/interface";
import { HierarchyUpdateDto } from "functions/api/hierarchy/saveHierarchyData";
import { toHierarchyUpdateDto } from "functions/api/hierarchy/toHierarchyUpdateDto";
import { isEqual } from "lodash";
import { app_saveTlfCompleted } from "./app_saveTlfCompleted";
import { IAppActionSaveCompleted, IUnifiedState, SAVE_TLF_COMPLETED } from "./interface";
import { reduceHierarchySaveBlockers } from "./saveBlockers";

/**
 * Reconciles either kind of Save response atomically, retaining later edits and route identity.
 *
 * @param state - Latest application state, which may include edits made during the request.
 * @param action - Server result and the hierarchy/editor snapshots submitted with the request.
 * @returns Reconciled state, or the existing state when the submitting route is no longer active.
 */
export const app_saveCompleted = (
  state: IUnifiedState,
  action: IAppActionSaveCompleted,
): IUnifiedState => {
  if (
    state.hierarchyState.hierarchy?.id !== action.submittedHierarchy.id ||
    state.tlfState.tlfData?.id !== action.submittedTlfId
  )
    return state;
  if (action.result.tlfItem && action.submittedTlfId !== undefined) {
    return app_saveTlfCompleted(state, {
      ...action,
      operation: SAVE_TLF_COMPLETED,
      result: { ...action.result, tlfItem: action.result.tlfItem },
      submittedTlfId: action.submittedTlfId,
    });
  }
  const returnedHierarchy = action.result.hierarchy;
  if (!returnedHierarchy) return state;
  const subsequentUpdates: Partial<HierarchyUpdateDto> = Object.fromEntries(
    Object.entries(toHierarchyUpdateDto(state.hierarchyState.hierarchy)).filter(
      ([key, value]) => !isEqual(value, action.submittedHierarchy[key as keyof HierarchyUpdateDto]),
    ),
  );
  const completedAction: IHierarchyActionSaveHierarchyCompleted = {
    operation: SAVE_HIERARCHY_COMPLETED,
    hierarchy: returnedHierarchy,
  };
  let updatedHierarchyState = hcr_saveHierarchyCompleted(state.hierarchyState, completedAction);
  if (Object.keys(subsequentUpdates).length > 0) {
    updatedHierarchyState = hcr_updateHierarchy(updatedHierarchyState, {
      operation: UPDATE_HIERARCHY,
      hierarchy: subsequentUpdates,
      isNameChanged: state.hierarchyState.isNameChanged,
    });
  }
  return {
    ...state,
    hierarchyState: updatedHierarchyState,
    saveBlockers: reduceHierarchySaveBlockers(
      state.saveBlockers,
      updatedHierarchyState,
      completedAction,
      state.allowedReportTypes,
    ),
  };
};
